// External dependencies
import get from 'lodash.get';
import React from 'react';
import FaPlayCircle from 'react-icons/lib/fa/play-circle';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import {getUserData} from '../../services/API';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";
import { prepareMicForPlayback, prepareMicForRecord } from '../audiocapture/recorder';

let videoTimer = null;
let videoTimeout = null;

class StudentVideo extends React.Component {

  constructor(props){
    super(props);
    this.onEnd = this.onEnd.bind(this);
    this.onError = this.onError.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.onLoadedMetadata = this.onLoadedMetadata.bind(this);
    this.onLoadedData = this.onLoadedData.bind(this);
    this.simplePlayVideo = this.simplePlayVideo.bind(this);
    this.playVideo = this.playVideo.bind(this)
    
    this.state = {
      clickable: getUserData().sessionData.useIpadFallbacks,
      playedVideo: null,
      requirePlayButton: false
    }
    videoTimeout = 0;
    
    this.htmlVideoPlayerRef = React.createRef();
  }

  componentDidMount(){
    videoTimer = null;

    if(this.props.autoPlay && !this.state.clickable){
      this.loadAndPlayVideo();
    }
  }

  componentDidUpdate(prevProps){
    if(!this.state.clickable){
      if(prevProps.videoURL != this.props.videoURL && !this.props.videoURL){
        this.cleanUp();
      }if(prevProps.videoURL != this.props.videoURL && this.props.videoURL){
        if(this.props.autoPlay){
          this.cleanUp();
          this.loadAndPlayVideo();
        }else{
          this.cleanUp();
          this.loadVideo();
        }
      }
      if(!prevProps.autoPlay && this.props.autoPlay && this.props.videoURL){
        this.playVideo();
      }
    }
  }

  onLoadedMetadata(event){
    if(this.htmlVideoPlayerRef.current.duration){
      videoTimeout = this.htmlVideoPlayerRef.current.duration*1000 + 5000;
      videoTimer = setTimeout(this.onEnd,videoTimeout);
    }
  }

  onLoadedData(event){
    this.playVideo();
  }

  simplePlayVideo(){
    this.props.speech.abortSpeech();
    let player = this.htmlVideoPlayerRef.current;
    player.play().catch((e)=>{
      const errorMessage = get(e, 'message')
      if(typeof errorMessage === 'string' && errorMessage.includes('The request is not allowed by the user agent')){
        console.log("handling play video error - will try to play with a play button", errorMessage)
        this.props.speech.speak({
          textToSpeak: COPY_STRINGS.play_video_with_button,
        })
        this.setState({requirePlayButton: true})
      }else{
        console.log("play video error", e)
      }
    })
  }

  playVideo(){
    //don't allow the same video to replay
    if((this.props.videoURL == this.state.playedVideo) && !this.props.allowReplay){
      return;
    }

    prepareMicForPlayback();

    //If we know the duration of the video, set the timeout. Otherwise set an event handler to grab it
    // when the metadata loads.
    if(this.htmlVideoPlayerRef.current.duration){
      videoTimeout = this.htmlVideoPlayerRef.current.duration*1000 + 5000;
      videoTimer = setTimeout(this.onEnd,videoTimeout);
    }else{
      //If the metadata isn't available yet, set an event handler so we can set the timeout once we have duration
      this.htmlVideoPlayerRef.current.addEventListener('loadedmetadata', this.onLoadedMetadata);
    }

    let player = this.htmlVideoPlayerRef.current;
    this.props.speech.abortSpeech();
    this.setState({playedVideo: this.props.videoURL}, () => {
      player.play().catch((e)=>{
        prepareMicForRecord();
        console.log("play video error", e)
        const errorMessage = get(e, 'message')
        if(typeof errorMessage === 'string' && errorMessage.includes('The request is not allowed by the user agent')){
          console.log("handling play video error - will try to play with a play button", errorMessage)
          this.props.speech.speak({
            textToSpeak: COPY_STRINGS.play_video_with_button,
          })
          this.setState({requirePlayButton: true})
        }
      })
    });
  }

  loadAndPlayVideo(){
    let player = this.htmlVideoPlayerRef.current;
    //Set an event handler so we play the video once the data is loaded.
    this.htmlVideoPlayerRef.current.addEventListener('loadeddata', this.onLoadedData);
    player.load();
  }

  loadVideo(){
    let player = this.htmlVideoPlayerRef.current;
    player.load();
  }

  cleanUp(){
    if(videoTimer){
      window.clearTimeout(videoTimer);
    }


    videoTimeout = 0;

    let player = this.htmlVideoPlayerRef.current;
    player.removeEventListener('loadeddata', this.onLoadedData);
    player.removeEventListener('loadedmetadata', this.onLoadedMetadata);
    if(this.props.unmountWhenDone){
      let player = this.htmlVideoPlayerRef.current;
      player.pause();
      player.removeAttribute('src'); // empty source
      player.load();
      //player.remove(); for now leave the element itself there so that the look of the UI remains more consistant
    }
  }

  onEnd(){
    prepareMicForRecord();
    this.cleanUp();

    if(this.props.onDone){
      this.props.onDone();
    }
  }

  onError(){
    prepareMicForRecord();
    if(this.props.onError){
      this.props.onError();
    }
  }

  preventEvent(e){
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  }

  //Keep the user from clicking on the video for play/pause/full screen
  handleClick(e){
    this.preventEvent(e);
    if(this.state.requirePlayButton){
      this.cleanUp();
      this.simplePlayVideo()
    } else if(this.state.clickable){
      this.playVideo();
    }
    this.setState({
      clickable: false,
      requirePlayButton: false
    });
  }

  componentWillUnmount(){
    this.cleanUp();
  }

  render() {
    let videoURL = this.props.videoURL;
    if((this.props.videoURL) && process.env.ASSET_ROOT){
      videoURL = this.props.videoURL.replace("s3.amazonaws.com/amira-assets",process.env.ASSET_ROOT);
    }

    return (
      <div className="studentVideoPlayer"
           {...doNotUseAccessibleStaticElementInteractions({
             onClick: this.handleClick,
             onDoubleClick:this.preventEvent,
             onMouseOver: this.state.clickable ? null : this.preventEvent
           })}>
        {(this.state.clickable || this.state.requirePlayButton) ?
          <div className="playButtonOverlay">
            <FaPlayCircle className="playButton" size="24"
              {...doNotUseAccessibleStaticElementInteractions({onClick: this.handleClick})}/>
          </div>
        : null}
        <video
          playsInline
          className="htmlVideoPlayer"
          onEnded={this.onEnd}
          //onPause={this.onEnd} //this is ok for now because we are not allowing users to pause the video
          onError={this.onError}
          key="theOnlyHtmlVideoPlayer"
          ref={this.htmlVideoPlayerRef} // Use the new ref created with React.createRef()
          volume={1}>
          <source src={videoURL} type="video/mp4"/>
        </video>
      </div>
    );
  }
}

StudentVideo.defaultProps = {
  autoPlay: true,
}

export default StudentVideo;